import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  BarChart3, TrendingUp, Users, DollarSign, Calendar, Download,
  PieChart, Activity, ArrowUp, ArrowDown
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReportsPage = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [pipeline, setPipeline] = useState([]);
  const [conversion, setConversion] = useState([]);
  const [revenue, setRevenue] = useState([]);
  const [customerGrowth, setCustomerGrowth] = useState([]);
  const [activitySummary, setActivitySummary] = useState([]);

  useEffect(() => {
    fetchAllReports();
  }, []);

  const fetchAllReports = async () => {
    try {
      const [dashRes, pipelineRes, conversionRes, revenueRes, growthRes, activityRes] = await Promise.all([
        fetch(`${API_URL}/api/reports/dashboard`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/reports/sales/pipeline`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/reports/sales/conversion?months=6`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/reports/sales/revenue?months=12`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/reports/customers/growth?months=12`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/reports/activities/summary?days=30`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);

      if (dashRes.ok) setDashboard(await dashRes.json());
      if (pipelineRes.ok) setPipeline(await pipelineRes.json());
      if (conversionRes.ok) setConversion(await conversionRes.json());
      if (revenueRes.ok) setRevenue(await revenueRes.json());
      if (growthRes.ok) setCustomerGrowth(await growthRes.json());
      if (activityRes.ok) setActivitySummary(await activityRes.json());
    } catch (error) {
      console.error('Error fetching reports:', error);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await fetch(`${API_URL}/api/reports/export/summary`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `crm_report_${new Date().toISOString().split('T')[0]}.json`;
        a.click();
        toast.success('Report exported');
      }
    } catch (error) {
      toast.error('Export failed');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  // Calculate max values for bar charts
  const maxPipelineValue = Math.max(...pipeline.map(p => p.total_value), 1);
  const maxRevenueValue = Math.max(...revenue.map(r => r.revenue), 1);
  const maxGrowthValue = Math.max(...customerGrowth.map(g => g.cumulative), 1);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Reports & Analytics</h1>
            <p className="text-gray-600 dark:text-gray-300">Track your business performance</p>
          </div>
          <Button onClick={handleExport} data-testid="export-report-btn">
            <Download className="w-4 h-4 mr-2" />
            Export Report
          </Button>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Total Customers</p>
                  <p className="text-2xl font-bold">{dashboard?.customers?.total || 0}</p>
                  <p className="text-xs text-green-600">
                    +{dashboard?.customers?.new_this_month || 0} this month
                  </p>
                </div>
                <div className="bg-blue-100 p-3 rounded-lg">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Pipeline Value</p>
                  <p className="text-2xl font-bold">${(dashboard?.opportunities?.pipeline_value || 0).toLocaleString()}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {dashboard?.opportunities?.open || 0} open deals
                  </p>
                </div>
                <div className="bg-green-100 p-3 rounded-lg">
                  <DollarSign className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Won This Month</p>
                  <p className="text-2xl font-bold text-green-600">
                    ${(dashboard?.opportunities?.won_revenue_month || 0).toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {dashboard?.opportunities?.won_deals_month || 0} deals closed
                  </p>
                </div>
                <div className="bg-emerald-100 p-3 rounded-lg">
                  <TrendingUp className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Active Projects</p>
                  <p className="text-2xl font-bold">{dashboard?.projects?.active || 0}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {dashboard?.bookings?.upcoming || 0} upcoming bookings
                  </p>
                </div>
                <div className="bg-purple-100 p-3 rounded-lg">
                  <Calendar className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row 1 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Sales Pipeline */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Sales Pipeline
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {pipeline.map((stage) => (
                  <div key={stage.stage}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="capitalize">{stage.stage}</span>
                      <span className="font-medium">${stage.total_value.toLocaleString()}</span>
                    </div>
                    <div className="h-6 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${
                          stage.stage === 'won' ? 'bg-green-500' :
                          stage.stage === 'lost' ? 'bg-red-500' :
                          stage.stage === 'lead' ? 'bg-gray-400' :
                          stage.stage === 'qualified' ? 'bg-blue-400' :
                          stage.stage === 'proposal' ? 'bg-yellow-400' :
                          'bg-purple-400'
                        }`}
                        style={{ width: `${(stage.total_value / maxPipelineValue) * 100}%` }}
                      ></div>
                    </div>
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>{stage.count} deals</span>
                      <span>Avg: ${Math.round(stage.avg_value).toLocaleString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Revenue Over Time */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Revenue Over Time
              </CardTitle>
            </CardHeader>
            <CardContent>
              {revenue.length > 0 ? (
                <div className="space-y-2">
                  <div className="flex items-end gap-1 h-48">
                    {revenue.map((month, index) => (
                      <div key={index} className="flex-1 flex flex-col items-center">
                        <div
                          className="w-full bg-green-500 rounded-t"
                          style={{ height: `${(month.revenue / maxRevenueValue) * 180}px` }}
                        ></div>
                        <span className="text-xs text-gray-500 mt-1 rotate-45 origin-left">
                          {month.month?.slice(5)}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between pt-4 border-t">
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Total Revenue</p>
                      <p className="text-lg font-bold">
                        ${revenue.reduce((sum, m) => sum + m.revenue, 0).toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Total Deals</p>
                      <p className="text-lg font-bold">
                        {revenue.reduce((sum, m) => sum + m.deals, 0)}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="h-48 flex items-center justify-center text-gray-500 dark:text-gray-400">
                  No revenue data yet
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Charts Row 2 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Conversion Rate */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <PieChart className="w-5 h-5" />
                Conversion Rates
              </CardTitle>
            </CardHeader>
            <CardContent>
              {conversion.length > 0 ? (
                <div className="space-y-3">
                  {conversion.map((month, index) => (
                    <div key={index} className="flex items-center gap-4">
                      <span className="text-sm w-16">{month.month?.slice(5)}</span>
                      <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-blue-500 rounded-full"
                          style={{ width: `${month.conversion_rate}%` }}
                        ></div>
                      </div>
                      <span className="text-sm font-medium w-12 text-right">
                        {month.conversion_rate}%
                      </span>
                    </div>
                  ))}
                  <div className="pt-4 border-t mt-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500 dark:text-gray-400">Average Conversion</span>
                      <span className="font-bold">
                        {conversion.length > 0
                          ? (conversion.reduce((sum, m) => sum + m.conversion_rate, 0) / conversion.length).toFixed(1)
                          : 0}%
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="h-48 flex items-center justify-center text-gray-500 dark:text-gray-400">
                  No conversion data yet
                </div>
              )}
            </CardContent>
          </Card>

          {/* Customer Growth */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="w-5 h-5" />
                Customer Growth
              </CardTitle>
            </CardHeader>
            <CardContent>
              {customerGrowth.length > 0 ? (
                <div className="space-y-2">
                  <div className="flex items-end gap-1 h-48">
                    {customerGrowth.map((month, index) => (
                      <div key={index} className="flex-1 flex flex-col items-center">
                        <div
                          className="w-full bg-blue-500 rounded-t"
                          style={{ height: `${(month.cumulative / maxGrowthValue) * 180}px` }}
                        ></div>
                        <span className="text-xs text-gray-500 mt-1 rotate-45 origin-left">
                          {month.month?.slice(5)}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between pt-4 border-t">
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Total Customers</p>
                      <p className="text-lg font-bold">
                        {customerGrowth.length > 0 ? customerGrowth[customerGrowth.length - 1].cumulative : 0}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Growth Rate</p>
                      <p className="text-lg font-bold text-green-600">
                        +{customerGrowth.reduce((sum, m) => sum + m.new_customers, 0)}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="h-48 flex items-center justify-center text-gray-500 dark:text-gray-400">
                  No customer growth data yet
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Activity Summary */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Activity className="w-5 h-5" />
              Activity Summary (Last 30 Days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {activitySummary.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {activitySummary.map((activity) => (
                  <div key={activity.type} className="text-center p-4 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold">{activity.count}</p>
                    <p className="text-sm text-gray-600 capitalize">{activity.type.replace('_', ' ')}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                No activity recorded in the last 30 days
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default ReportsPage;
